import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container } from '@legion-ui/core';
import Button from '@/components/Button';
import EmailEditor from '@/components/EmailEditor';
import { useCreateEmailBlast } from '@/hooks/useEmailBlastQuery';
import { notify } from '@/services/notify';
import useLoadingStore from '@/store/loading';
import { ToastContainer } from 'react-toastify';
import LoadingOverlay from '@/components/LoadingOverlay';

const CreateEmailBlast: React.FC = () => {
  const navigate = useNavigate();
  const [subject, setSubject] = useState('');
  const [bodyEmail, setBodyEmail] = useState('');
  const createEmailBlastMutation = useCreateEmailBlast();
  const { setLoading } = useLoadingStore();

  const handleSaveAsDraft = () => {
    if (!subject.trim() || !bodyEmail.trim()) {
      notify('Please fill in both Subject and Body Email fields before saving as draft.', { 
        type: 'error' 
      });
      return;
    }

    setLoading(true); // Show loading overlay

    createEmailBlastMutation.mutate(
      {
        subject: subject.trim(),
        content: bodyEmail.trim(),
        recipients: [], // Empty recipients for draft
      },
      {
        onSuccess: (response) => {
          notify('Draft saved successfully!', { type: 'success' });
          
          // Delay navigation to allow toast to be visible
          setTimeout(() => {
            setLoading(false); // Hide loading overlay
            navigate('/email-blast', { replace: true });
          }, 2000);
        },
        onError: (error) => {
          setLoading(false); // Hide loading overlay on error
          console.error('Failed to save draft:', error);
          notify('Failed to save draft. Please try again.', { type: 'error' });
        },
      }
    );
  };

  const handleAddRecipient = () => {
    if (!subject.trim() || !bodyEmail.trim()) {
      notify('Please fill in both Subject and Body Email fields before adding recipients.', {
        type: 'error'
      });
      return;
    }

    setLoading(true); // Show loading overlay

    // Save as draft first
    createEmailBlastMutation.mutate(
      {
        subject: subject.trim(),
        content: bodyEmail.trim(),
        recipients: [], // Empty recipients for draft
      },
      {
        onSuccess: (response) => {
          setLoading(false); // Hide loading overlay
          console.log('Create email blast response:', response);
          
          // Get ID from response - try different possible structures
          const emailBlastId = response?.data?.id || response?.id;
          
          if (!emailBlastId) {
            console.error('Email blast ID not found in response:', response);
            notify('Failed to get email blast ID. Please try again.', { type: 'error' });
            return;
          }
          
          // Navigate to add recipients page with the created email blast ID
          navigate('/email-blast/add-recipients', {
            state: {
              emailBlastId: emailBlastId,
              subject: subject.trim(),
              content: bodyEmail.trim(),
              existingRecipients: [],
            }
          });
        },
        onError: (error) => {
          setLoading(false); // Hide loading overlay on error
          console.error('Failed to save draft:', error);
          notify('Failed to save draft. Please try again.', { type: 'error' });
        },
      }
    );
  };

  return (
    <Container>
      <Box className="bg-white min-h-screen">
        {/* Header */}
        <Box className="border-b border-gray-200 px-6 py-4">
          <h1 className="text-2xl font-bold text-gray-900">Create Bulk Email</h1>
          <p className="text-sm text-gray-600 mt-1">Manage Sent email notification</p>
        </Box>

        {/* Content */}
        <Box className="px-6 py-6">
          <Box className="bg-gray-50 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Compose Email</h2>

            {/* Subject Input */}
            <Box className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subject <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => {
                  if (e.target.value.length <= 60) {
                    setSubject(e.target.value);
                  }
                }}
                placeholder="Example : Maintenance Developer portal"
                maxLength={60}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                {subject.length}/60 characters
              </p>
            </Box>

            {/* Body Email Editor */}
            <div className="mb-6 pb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Body Email <span className="text-red-500">*</span>
              </label>
              <EmailEditor value={bodyEmail} onChange={setBodyEmail} />
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap justify-end gap-4 mt-8 pt-4">
              <Button
                variant="outline"
                size="md"
                onClick={handleSaveAsDraft}
                className="px-6"
              >
                Save as Draft
              </Button>
              <Button
                variant="outline"
                size="md"
                onClick={handleAddRecipient}
                className="px-6"
              >
                Add Recipient
              </Button>
            </div>
          </Box>
        </Box>
      </Box>
      <LoadingOverlay />
      <ToastContainer />
    </Container>
  );
};

export default CreateEmailBlast;